import React from 'react';
import { useWorkspace } from '../../lib/workspace';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// ── Shared Excel/PDF utilities exported for all report pages ─────────────────

export const ExcelIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect width="16" height="16" rx="2" fill="#217346"/>
    <path d="M4 4.5L6.8 8L4 11.5H5.8L8 8.7L10.2 11.5H12L9.2 8L12 4.5H10.2L8 7.3L5.8 4.5H4Z" fill="white"/>
    <rect x="9" y="4.5" width="3" height="7" rx="0.5" fill="white" opacity="0.25"/>
  </svg>
);

export const PdfIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect width="16" height="16" rx="2" fill="#DC2626"/>
    <path d="M4 3h5.5L12 5.5V13H4V3Z" fill="white" opacity="0.25"/>
    <path d="M9 3v3h3" stroke="white" strokeWidth="1" fill="none"/>
    <text x="3.5" y="11.5" fontSize="4.5" fontWeight="700" fill="white" fontFamily="Arial">PDF</text>
  </svg>
);

export const iconBtnBase = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '7px 14px', borderRadius: 7, fontWeight: 600, fontSize: 13,
  cursor: 'pointer', border: '1px solid var(--border)', transition: 'opacity 0.15s',
};

export const excelBtnStyle = {
  ...iconBtnBase,
  background: '#f0faf4', color: '#166534', borderColor: '#bbf7d0',
};

export const pdfBtnStyle = {
  border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b',
};

// Capitalizes the first letter of each word in a filename while leaving the
// extension, digits, and separators untouched (report-2026.xls → Report-2026.xls).
function capitalizeFilename(name) {
  const dot = name.lastIndexOf('.');
  const base = dot >= 0 ? name.slice(0, dot) : name;
  const ext  = dot >= 0 ? name.slice(dot) : '';
  const capitalized = base
    .split(/([^a-zA-Z0-9]+)/)
    .map(part => (/^[a-zA-Z]/.test(part) ? part[0].toUpperCase() + part.slice(1) : part))
    .join('');
  return capitalized + ext;
}

export function downloadExcel(filename, title, subtitle, headers, rows) {
  const esc = v => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const colCount = headers.length;
  const colWidths = headers.map((h, i) => {
    const maxLen = Math.max(String(h).length, ...rows.map(r => String(r[i] ?? '').length));
    return Math.min(Math.max(maxLen * 8, 70), 320);
  });
  const colgroup = colWidths.map(w => `<col width="${w}">`).join('');
  const titleRow = `<tr><td colspan="${colCount}" style="color:#000;font-size:16px;font-weight:700;padding:14px 12px 4px;border:none;text-align:center;">${esc(title)}</td></tr>`;
  const subtitleRow = subtitle ? `<tr><td colspan="${colCount}" style="color:#444;font-size:11px;font-weight:400;padding:2px 12px 10px;border:none;text-align:center;">${esc(subtitle)}</td></tr>` : '';
  const spacerRow = `<tr><td colspan="${colCount}" style="padding:3px;border:none;"></td></tr>`;
  const headerRow = `<tr>${headers.map(h =>
    `<th style="background:#2563eb;color:#fff;font-weight:700;padding:8px 12px;border:1px solid #1d4ed8;white-space:nowrap;font-size:12px;text-transform:uppercase;">${esc(h)}</th>`
  ).join('')}</tr>`;
  const bodyRows = rows.map((row, ri) => {
    const bg = ri % 2 === 0 ? '#ffffff' : '#f1f5f9';
    return `<tr>${row.map(cell =>
      `<td style="background:${bg};padding:6px 12px;border:1px solid #e2e8f0;white-space:nowrap;font-size:13px;">${esc(cell)}</td>`
    ).join('')}</tr>`;
  }).join('');
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
    xmlns:x="urn:schemas-microsoft-com:office:excel"
    xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8">
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>Report</x:Name><x:WorksheetOptions><x:DisplayGridlines/>
</x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head><body>
<table border="0" cellspacing="0" style="font-family:Calibri,Arial,sans-serif;border-collapse:collapse;">
<colgroup>${colgroup}</colgroup>
<thead>${titleRow}${subtitleRow}${spacerRow}${headerRow}</thead>
<tbody>${bodyRows}</tbody>
</table></body></html>`;
  const blob = new Blob(['﻿' + html], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = capitalizeFilename(filename); a.click();
  URL.revokeObjectURL(url);
}

const API_HOST = (process.env.REACT_APP_API_URL || 'http://localhost:8080/api').replace(/\/api$/, '');
export function fullLogoUrl(path) { return path ? `${API_HOST}${path}` : null; }

function nowLabel() {
  return new Date().toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function slugify(str) {
  return (str || 'Report')
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map(w => w[0].toUpperCase() + w.slice(1))
    .join('-');
}

async function loadImageAsBase64(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({ data: canvas.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// Headers and cell values in these columns must never line-break
const noWrapPattern = /date|time|hour|\bh\b|log\b|logged|est\.|period|s\.no|task\s*#|status|priority|score|%|weightage|entries|deadline|checklist|days|frequency|expected|done|assigned|pending|overdue|progress|milestones|tasks|total|approved|draft|base|employees|completion|start|end|created|#$/i;

// Builds and downloads a branded PDF report (logo/accent header, optional
// summary cards, one or more autoTable sections with page footers). Shared
// by <ReportPdfButton> and any other caller that needs a report PDF without
// rendering the button itself (e.g. a menu action).
export async function buildReportPdf({
  orgName,
  logoSrc,
  title,
  subtitle,
  summaryCards = [],
  table,        // { headers: string[], rows: any[][] }
  sections,     // [{ title, headers, rows }] — overrides table
  columnStyles, // { [colIndex]: { cellWidth, ... } } passed to autoTable
  wrapCols,     // [colIndex, ...] columns that should hard-wrap long no-space strings
  orientation = 'landscape',
}) {
    const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 14;
    let y = margin;

    // ── Header ──────────────────────────────────────────────────────────────
    let logoLoaded = null;
    if (logoSrc) {
      logoLoaded = await loadImageAsBase64(logoSrc);
    }

    // Accent bar
    doc.setFillColor(6, 182, 212);
    doc.rect(0, 0, pageW, 1.5, 'F');

    y = 8;

    if (logoLoaded) {
      const maxH = 12;
      const ratio = logoLoaded.w / logoLoaded.h;
      const lw = maxH * ratio;
      doc.addImage(logoLoaded.data, 'PNG', margin, y, lw, maxH);
      doc.setFontSize(13).setFont('helvetica', 'bold').setTextColor(30, 30, 30);
      doc.text(orgName, margin + lw + 4, y + 7);
      y += maxH + 4;
    } else {
      doc.setFontSize(14).setFont('helvetica', 'bold').setTextColor(30, 30, 30);
      doc.text(orgName, margin, y + 6);
      y += 12;
    }

    // Report title
    doc.setFontSize(11).setFont('helvetica', 'bold').setTextColor(6, 182, 212);
    doc.text(title.toUpperCase(), margin, y);
    y += 5;

    // Subtitle
    if (subtitle) {
      doc.setFontSize(9).setFont('helvetica', 'normal').setTextColor(100, 100, 100);
      doc.text(subtitle, margin, y);
      y += 5;
    }

    // Generated date (right-aligned)
    doc.setFontSize(8).setFont('helvetica', 'italic').setTextColor(150, 150, 150);
    doc.text(`Generated: ${nowLabel()}`, pageW - margin, 10, { align: 'right' });

    // Divider
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y, pageW - margin, y);
    y += 5;

    // ── Summary cards ────────────────────────────────────────────────────────
    if (summaryCards.length > 0) {
      // Size each card to its content so long values like dates never overflow
      const cardWidths = summaryCards.map(c => {
        doc.setFontSize(7).setFont('helvetica', 'bold');
        const labelW = doc.getTextWidth(String(c.label).toUpperCase());
        doc.setFontSize(13).setFont('helvetica', 'bold');
        const valueW = doc.getTextWidth(String(c.value ?? ''));
        return Math.max(28, Math.ceil(Math.max(labelW, valueW) + 6));
      });
      // Wrap to a new row instead of running off the page edge — matters on
      // narrower pages (portrait) where all cards rarely fit on one line.
      const cardH = 14;
      const gap = 4;
      let cx = margin;
      let rowY = y;
      summaryCards.forEach((c, idx) => {
        const cardW = cardWidths[idx];
        if (cx !== margin && cx + cardW > pageW - margin) {
          cx = margin;
          rowY += cardH + gap;
        }
        doc.setFillColor(245, 247, 250);
        doc.roundedRect(cx, rowY, cardW, cardH, 2, 2, 'F');
        doc.setFontSize(7).setFont('helvetica', 'bold').setTextColor(120, 120, 120);
        doc.text(String(c.label).toUpperCase(), cx + 3, rowY + 5);
        const hex = c.color && c.color.startsWith('#') ? c.color : '#1e1e1e';
        const r = parseInt(hex.slice(1, 3), 16) || 30;
        const g = parseInt(hex.slice(3, 5), 16) || 30;
        const b = parseInt(hex.slice(5, 7), 16) || 30;
        doc.setFontSize(13).setFont('helvetica', 'bold').setTextColor(r, g, b);
        doc.text(String(c.value ?? ''), cx + 3, rowY + 12);
        cx += cardW + gap;
      });
      y = rowY + cardH + 6;
    }

    // ── Table(s) ─────────────────────────────────────────────────────────────
    const tableSections = sections
      ? sections
      : table ? [{ title: null, headers: table.headers, rows: table.rows }] : [];

    for (const sec of tableSections) {
      // autoTable doesn't look ahead before starting: if the title+header
      // barely fit but no body row does, it draws them dangling at the
      // bottom of the page and repeats both at the top of the next one.
      // Force the break ourselves so a section always starts together
      // with at least one row of its own table.
      const minSpaceNeeded = (sec.title ? 8 : 0) + 22;
      if (y + minSpaceNeeded > pageH - margin) {
        doc.addPage();
        y = margin;
      }

      if (sec.title) {
        doc.setFontSize(9).setFont('helvetica', 'bold').setTextColor(6, 182, 212);
        doc.text(sec.title.toUpperCase(), margin, y);
        y += 5;
      }

      const body = (sec.rows || []).map(row =>
        row.map(cell => (cell == null ? '—' : String(cell)))
      );

      const colCount = sec.headers.length;
      const dense = colCount > 10;
      const fontSize = dense ? 7 : 9;
      const cellPad = dense ? { top: 3, right: 2.5, bottom: 3, left: 2.5 } : { top: 5, right: 7, bottom: 5, left: 7 };
      const headPad = dense ? { top: 3.5, right: 2.5, bottom: 3.5, left: 2.5 } : { top: 6, right: 7, bottom: 6, left: 7 };

      // Which column indices are no-wrap (dates, hours, short labels)
      const noWrapCols = new Set(
        sec.headers.map((h, i) => noWrapPattern.test(String(h)) ? i : -1).filter(i => i >= 0)
      );

      autoTable(doc, {
        startY: y,
        head: [sec.headers.map(h => String(h))],
        body,
        margin: { left: margin, right: margin },
        styles: {
          fontSize,
          cellPadding: cellPad,
          textColor: [30, 30, 30],
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
          overflow: 'linebreak',
          minCellWidth: dense ? 10 : undefined,
        },
        headStyles: {
          fillColor: [6, 182, 212],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize,
          cellPadding: headPad,
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
        columnStyles: columnStyles || {},
        didParseCell: (data) => {
          const colIdx = data.column.index;
          const raw = String(data.cell.raw ?? '');
          if (noWrapCols.has(colIdx) && data.cell.raw) {
            // prevent line-breaks in date/hour/status columns
            data.cell.text = [raw.replace(/ /g, ' ')];
          } else if ((wrapCols || []).includes(colIdx) && raw.length > 40) {
            // hard-wrap long no-space strings every 40 chars
            const chunks = [];
            for (let i = 0; i < raw.length; i += 40) chunks.push(raw.slice(i, i + 40));
            data.cell.text = chunks;
          }
        },
        didDrawPage: (data) => {
          const pCount = doc.internal.getNumberOfPages();
          doc.setFontSize(7).setFont('helvetica', 'normal').setTextColor(150, 150, 150);
          doc.text(`${orgName} — ${title}`, margin, doc.internal.pageSize.getHeight() - 6);
          doc.text(`Page ${data.pageNumber} of ${pCount}`, pageW - margin, doc.internal.pageSize.getHeight() - 6, { align: 'right' });
        },
      });

      y = doc.lastAutoTable.finalY + 8;
    }

    doc.save(`${slugify(title)}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

export default function ReportPdfButton({
  title,
  subtitle,
  summaryCards = [],
  table,        // { headers: string[], rows: any[][] }
  sections,     // [{ title, headers, rows }] — overrides table
  columnStyles, // { [colIndex]: { cellWidth, ... } } passed to autoTable
  wrapCols,     // [colIndex, ...] columns that should hard-wrap long no-space strings
  buttonStyle,
  icon,
}) {
  const { organisation } = useWorkspace();
  const orgName = organisation?.name || '';
  const logoSrc = organisation?.logo_data || fullLogoUrl(organisation?.logo_url);

  const handleDownload = () => buildReportPdf({
    orgName, logoSrc, title, subtitle, summaryCards, table, sections, columnStyles, wrapCols,
  });

  return (
    <button
      onClick={handleDownload}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '7px 14px', borderRadius: 7,
        border: icon ? '1px solid #fecaca' : '1px solid var(--border)',
        background: icon ? '#fef2f2' : 'var(--bg-card)',
        color: icon ? '#991b1b' : 'var(--text-primary)',
        fontSize: 13, fontWeight: 600, cursor: 'pointer',
        transition: 'opacity 0.15s',
        ...buttonStyle,
      }}
      title="Download as PDF"
    >
      {icon || null} {icon ? 'PDF' : '⬇ Download PDF'}
    </button>
  );
}
